// Third-party
import React, { ReactNode, Component } from 'react';
import throttle from 'lodash/throttle';
import classNames from 'classnames';
import Draggable from 'react-draggable';

type Props = {
  children?: ReactNode;
  scrollContainerRef?: HTMLElement;
  className?: string;
};

type State = {
  dragging: boolean;
  windowWidth: number;
  windowHeight: number;
  windowX: number;
  startX?: number;
  startScrollLeft: number;
  interval?: NodeJS.Timeout;
};

class WindowScroller extends Component<Props, State> {
  private windowScrollerRef: HTMLDivElement | null = null;

  readonly state: State = {
    dragging: false,
    windowWidth: 0,
    windowHeight: 0,
    windowX: 0,
    startScrollLeft: 0,
  };

  setWindowDimensions = () => {
    const { scrollContainerRef } = this.props;

    if (!scrollContainerRef || !this.windowScrollerRef) {
      return;
    }

    const { offsetWidth, scrollWidth } = scrollContainerRef;

    const scrollViewFraction = offsetWidth / scrollWidth;
    const windowWidth = this.windowScrollerRef.offsetWidth * scrollViewFraction;
    const windowHeight = this.windowScrollerRef.offsetHeight;

    this.setState({ windowWidth, windowHeight });
  };

  setWindowX = () => {
    const { scrollContainerRef } = this.props;

    if (!scrollContainerRef || !this.windowScrollerRef) {
      return;
    }
    const { scrollLeft, scrollWidth } = scrollContainerRef;
    const scrollLeftFraction = scrollLeft / scrollWidth;
    const windowX = this.windowScrollerRef.offsetWidth * scrollLeftFraction;

    this.setState({ windowX });
  };

  handleResize = throttle(() => {
    this.setWindowDimensions();
    this.setWindowX();
  }, 30);

  handleScroll = throttle(() => {
    this.setWindowX();
  }, 20);

  componentDidMount() {
    const { scrollContainerRef } = this.props;

    if (!scrollContainerRef) {
      return;
    }

    this.setWindowDimensions();
    window.addEventListener('resize', this.handleResize);
    window.addEventListener('mouseup', this.handleMouseUp);
    scrollContainerRef.addEventListener('scroll', this.handleScroll);
  }

  componentWillUnmount() {
    const { scrollContainerRef } = this.props;

    if (!scrollContainerRef) {
      return;
    }

    window.removeEventListener('resize', this.handleResize);
    window.removeEventListener('mouseup', this.handleMouseUp);
    scrollContainerRef.removeEventListener('scroll', this.handleScroll);
  }

  componentDidUpdate(prevProps: Props) {
    const { children } = this.props;

    if (prevProps.children !== children) {
      this.setWindowDimensions();
      this.setWindowX();
    }
  }

  getWindowScrollerRef = () => this.windowScrollerRef;

  handleMouseDown = (e: React.MouseEvent) => {
    const { scrollContainerRef } = this.props;
    const { windowWidth } = this.state;

    if (!scrollContainerRef || !this.windowScrollerRef) {
      return;
    }

    const SCROLL_SPEED = 80;
    const OVERSCROLL_DISTANCE = windowWidth / 5;
    const mouseX = e.clientX - this.windowScrollerRef.offsetLeft;

    const interval = setInterval(() => {
      const { windowX } = this.state;

      if (mouseX > windowX + windowWidth - OVERSCROLL_DISTANCE) {
        scrollContainerRef.scrollLeft += SCROLL_SPEED;
      }

      if (mouseX < windowX + OVERSCROLL_DISTANCE) {
        scrollContainerRef.scrollLeft -= SCROLL_SPEED;
      }
    }, 10);

    this.setState({ interval });
  };

  handleMouseUp = () => {
    if (this.state.interval) {
      clearInterval(this.state.interval);
    }
  };

  handleStart = (e: React.MouseEvent) => {
    const { scrollContainerRef } = this.props;

    if (!scrollContainerRef) {
      return;
    }

    this.setState({ dragging: true, startX: e.clientX, startScrollLeft: scrollContainerRef.scrollLeft });
  };

  handleStop = () => {
    this.setState({ dragging: false, startX: undefined });
  };

  handleDrag = throttle((e: React.MouseEvent) => {
    const { dragging, startX, startScrollLeft } = this.state;
    const { scrollContainerRef } = this.props;

    if (!dragging || !startX || !scrollContainerRef || !this.windowScrollerRef) {
      return;
    }

    const windowScrollerWidth = this.windowScrollerRef.offsetWidth;
    const diff = e.clientX - startX;

    const scrollPercentage = diff / windowScrollerWidth;
    const relativeScrollInTable = scrollContainerRef.scrollWidth * scrollPercentage;

    scrollContainerRef.scrollLeft = startScrollLeft + relativeScrollInTable;
  }, 20);

  render() {
    const { children, className } = this.props;
    const { windowWidth, windowHeight, windowX, dragging } = this.state;

    const style = {
      width: windowWidth,
      height: windowHeight,
    };

    return (
      <div
        ref={ref => {
          this.windowScrollerRef = ref;
        }}
        className={classNames('window-scroller d-flex', className)}
      >
        <span className="window-scroller-content d-flex" onMouseDown={e => this.handleMouseDown(e)}>
          {children}
        </span>

        <Draggable
          axis="x"
          handle=".window"
          bounds=".window-scroller"
          onStart={e => this.handleStart(e as React.MouseEvent)}
          onDrag={e => this.handleDrag(e as React.MouseEvent)}
          onStop={this.handleStop}
          position={{ x: windowX, y: 0 }}
        >
          <div className={classNames('window', { dragging })} style={style} />
        </Draggable>
      </div>
    );
  }
}

export default WindowScroller;
